// next-app/src/components/survey/survey-form.tsx
'use client';

import { FormProvider, UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface SurveyFormProps<T extends Record<string, unknown>> {
  form: UseFormReturn<T>;
  onSubmit: (data: T) => Promise<void> | void;
  title: string;
  description?: string;
  eventName?: string;
  dealerName?: string;
  children: React.ReactNode;
  isSubmitting?: boolean;
  submitText?: string;
}

/**
 * Survey form wrapper component.
 * Provides form context, header, and submit button.
 */
export function SurveyForm<T extends Record<string, unknown>>({
  form,
  onSubmit,
  title,
  description,
  eventName,
  dealerName,
  children,
  isSubmitting = false,
  submitText = 'Submit Survey',
}: SurveyFormProps<T>) {
  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mx-auto max-w-2xl space-y-6">
        {/* Survey header */}
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
            {(eventName || dealerName) && (
              <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                {eventName && <p>Event: {eventName}</p>}
                {dealerName && <p>Hosted by: {dealerName}</p>}
              </div>
            )}
          </CardHeader>
        </Card>

        {/* Survey sections */}
        <Card>
          <CardContent className="space-y-8 pt-6">{children}</CardContent>
        </Card>

        {/* Submit button */}
        <div className="flex justify-center">
          <Button type="submit" size="lg" className="min-w-48" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              submitText
            )}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
